import { useEffect, useMemo } from 'react';
import { useContentContext } from '../../contexts/ContentContext';

const FALLBACK_THEME = {
  brand: '#4F8EF7',
  accent: '#6366f1',
  page: '#050816',
};

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function validColor(value, fallback) {
  return typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : fallback;
}

function getTheme(globalContent = {}) {
  const brand = validColor(globalContent.primaryColor, FALLBACK_THEME.brand);
  const accent = validColor(globalContent.accentColor || globalContent.secondaryColor, FALLBACK_THEME.accent);
  const page = validColor(globalContent.bgColor || globalContent.backgroundColor, FALLBACK_THEME.page);

  return {
    '--ui-color-brand': brand,
    '--ui-color-accent': accent,
    '--ui-color-page': page,
    '--accent': brand,
    '--accent-bright': accent,
    '--bg-primary': page,
    '--gradient-accent': `linear-gradient(135deg, ${brand}, ${accent})`,
  };
}

/**
 * Applies the editable brand values as CSS variables at the document root.
 * New UI primitives consume --ui-* tokens; legacy styles can keep using the
 * compatibility aliases during the gradual migration.
 */
export default function BrandTheme({ children }) {
  const { content } = useContentContext();
  const variables = useMemo(() => getTheme(content?.global), [content?.global]);

  useEffect(() => {
    const root = document.documentElement;
    const themeColor = document.querySelector('meta[name="theme-color"]');
    const previousThemeColor = themeColor?.getAttribute('content');

    Object.entries(variables).forEach(([name, value]) => root.style.setProperty(name, value));
    root.dataset.uiTheme = 'klaivox';
    themeColor?.setAttribute('content', variables['--ui-color-page']);

    return () => {
      Object.keys(variables).forEach(name => root.style.removeProperty(name));
      delete root.dataset.uiTheme;
      if (previousThemeColor) themeColor?.setAttribute('content', previousThemeColor);
    };
  }, [variables]);

  return children;
}
